import { ResponseData, ResponseResult } from '~/core/api/ResponseTypes';
import { Configuration } from '~/core/configuration/configuration.type';
import { GeneralFilter } from '~/core/features/filtering/GeneralFilter';
import countResults from '~/utils/countResults';
import { State } from '../types/state';
import { findQuerySetData } from '../utils/findQuerySetData';

const applyAggregations = (state: State, config: Configuration, payload: ResponseData): State['sets'] => {
    const filter = new GeneralFilter();
    return state.sets.map((querySetState) => {
        const { querySetControls, querySetResults } = findQuerySetData<ResponseResult>(
            querySetState.querySet.identifier,
            {
                config,
                state,
                results: payload.results,
            }
        );
        if (!querySetResults || !querySetResults.aggregations) {
            return querySetState;
        }

        const filters = querySetState.filters.map((filterState) => {
            const filterAggregation = querySetResults.aggregations[filterState.identifier];
            const filterControls = querySetControls.filters.find(
                (filterControl) => filterControl.identifier === filterState.identifier
            );

            if (!filterControls) {
                throw new Error(`Filter controls for ${filterState.identifier} not found`);
            }

            if (!filterAggregation) {
                return filterState;
            }

            return filter.applyAggregation(filterState, filterControls, filterAggregation);
        });

        return {
            ...querySetState,
            filters,
        };
    });
};

/**
 * Updates state based on new search results, apply aggregations if present.
 */
const searchSuccess = (state: State, config: Configuration, payload: ResponseData): State => {
    const results = {
        totalRecordsCount: payload.results.reduce((acc, set) => acc + set.totalRecords, 0),
        filteredCategoriesRecordsCount: countResults(payload.results, ['serp-categories']),
        sets: payload.results,
    };
    const shouldUpdateSetsState = payload.results.some(
        (set) => set.aggregations && Object.keys(set.aggregations).length > 0
    );

    return {
        ...state,
        sets: shouldUpdateSetsState ? applyAggregations(state, config, payload) : state.sets,
        results,
        isLoading: false,
        loadingFinished: true,
    };
};

export default searchSuccess;
